import React, { useEffect, useRef } from "react";
import {
  useChartDatasets,
  useReportData,
  useReportGroups,
  useTimestampProcessing,
} from ".";
import { GraphData } from "../types";

const useChartData = ({
  usePromptId = true,
  useParentReportId = false,
  displayAll = false,
}: {
  usePromptId?: boolean;
  useParentReportId?: boolean;
  displayAll?: boolean;
}) => {
  const { reports, isLoading } = useReportData({
    usePromptId,
    useParentReportId,
    displayAll,
  });
  const reportGroups = useReportGroups(reports);
  const timestampData = useTimestampProcessing(reports);
  const datasets = useChartDatasets(reportGroups, timestampData);

  // Use ref to store previous data to prevent unnecessary updates
  const prevDataRef = useRef<{
    labels: string[];
    datasets: typeof datasets;
  } | null>(null);

  const [chartData, setChartData] = React.useState<GraphData>({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    if (!reports) return;

    const newData = {
      labels: timestampData.labels,
      datasets,
    };

    const prevData = prevDataRef.current;

    if (
      !prevData ||
      JSON.stringify(prevData.labels) !== JSON.stringify(newData.labels) ||
      prevData.datasets.length !== newData.datasets.length
    ) {
      setChartData(newData);
      prevDataRef.current = newData;
    }
  }, [reports, timestampData.labels, datasets]);

  return [chartData, isLoading] as const;
};

export default useChartData;
